import { useEffect, useRef, useState } from 'react'
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode'
import { Camera, Keyboard, Flashlight, FlashlightOff, X, Scan } from 'lucide-react'

type Props = {
  /** Callback quando um codigo eh lido (camera) ou enviado (teclado). */
  onScan: (codigo: string) => void
  /** Callback pra fechar o scanner. */
  onClose: () => void
  /** Ignora leituras duplicadas dentro desse intervalo (ms). Default 1200. */
  dedupMs?: number
}

const SCANNER_DIV_ID = 'pain-scanner-div'

export default function CameraScanner({ onScan, onClose, dedupMs = 1200 }: Props) {
  const [modo, setModo] = useState<'camera' | 'teclado'>('camera')
  const [torch, setTorch] = useState(false)
  const [erroCamera, setErroCamera] = useState<string | null>(null)
  const [valorTeclado, setValorTeclado] = useState('')
  const scannerRef = useRef<Html5Qrcode | null>(null)
  const ultimoLidoRef = useRef<{ codigo: string; ts: number }>({ codigo: '', ts: 0 })
  const trackRef = useRef<MediaStreamTrack | null>(null)

  // ============================================================
  // Monta / desmonta camera
  // ============================================================
  useEffect(() => {
    if (modo !== 'camera') return

    const scanner = new Html5Qrcode(SCANNER_DIV_ID, {
      formatsToSupport: [
        Html5QrcodeSupportedFormats.EAN_13,
        Html5QrcodeSupportedFormats.EAN_8,
        Html5QrcodeSupportedFormats.UPC_A,
        Html5QrcodeSupportedFormats.UPC_E,
        Html5QrcodeSupportedFormats.CODE_128,
        Html5QrcodeSupportedFormats.CODE_39,
        Html5QrcodeSupportedFormats.ITF,
        Html5QrcodeSupportedFormats.QR_CODE,
      ],
      verbose: false,
    })
    scannerRef.current = scanner

    scanner.start(
      { facingMode: 'environment' },
      {
        fps: 10,
        qrbox: (w, h) => ({
          width: Math.floor(Math.min(w, h) * 0.8),
          height: Math.floor(Math.min(w, h) * 0.4),
        }),
        aspectRatio: undefined,
        disableFlip: false,
      },
      (decodedText) => {
        const agora = Date.now()
        const { codigo: ult, ts } = ultimoLidoRef.current
        if (decodedText === ult && agora - ts < dedupMs) return
        ultimoLidoRef.current = { codigo: decodedText, ts: agora }
        // Feedback tatil
        try { navigator.vibrate?.(80) } catch {}
        onScan(decodedText)
      },
      () => { /* ignore decode failures */ }
    ).then(() => {
      // Tenta pegar o MediaStreamTrack pra saber se suporta torch
      try {
        const video = document.querySelector<HTMLVideoElement>(`#${SCANNER_DIV_ID} video`)
        const stream = (video?.srcObject as MediaStream | null)
        trackRef.current = stream?.getVideoTracks()[0] || null
      } catch {}
    }).catch((err) => {
      setErroCamera(String(err?.message || err || 'Erro ao iniciar camera'))
    })

    return () => {
      try {
        scanner.stop().then(() => scanner.clear()).catch(() => {})
      } catch {}
      scannerRef.current = null
      trackRef.current = null
    }
  }, [modo, onScan, dedupMs])

  // ============================================================
  // Torch
  // ============================================================
  async function toggleTorch() {
    const track = trackRef.current
    if (!track) return
    try {
      await track.applyConstraints({
        // @ts-expect-error — torch eh nao-padrao mas suportado em chrome android
        advanced: [{ torch: !torch }],
      })
      setTorch(!torch)
    } catch {
      // Ignora silenciosamente — nem todo device suporta
    }
  }

  // ============================================================
  // Teclado
  // ============================================================
  function enviarTeclado(e: React.FormEvent) {
    e.preventDefault()
    const v = valorTeclado.trim()
    if (!v) return
    onScan(v)
    setValorTeclado('')
  }

  // ============================================================
  // Render
  // ============================================================
  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">
      {/* Header preto */}
      <div className="flex items-center gap-2 p-3 text-white bg-black/70">
        <Scan size={18} />
        <span className="font-semibold text-sm flex-1">
          {modo === 'camera' ? 'Mire no código' : 'Digite o código'}
        </span>
        <button onClick={onClose} className="p-2 -mr-1 rounded hover:bg-white/10">
          <X size={22} />
        </button>
      </div>

      {/* Area principal */}
      <div className="flex-1 relative flex items-center justify-center overflow-hidden">
        {modo === 'camera' ? (
          <>
            <div id={SCANNER_DIV_ID} className="w-full h-full absolute inset-0" />
            {/* Overlay visual (html5-qrcode ja desenha o qrbox, mas adiciona linha vermelha) */}
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <div className="w-72 h-1 bg-red-500/70 shadow-[0_0_8px_rgba(239,68,68,0.8)] rounded-full animate-pulse" />
            </div>
            {erroCamera && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/80 p-4">
                <div className="bg-white rounded-lg p-4 max-w-sm text-center">
                  <Camera size={32} className="inline-block text-red-500 mb-2" />
                  <h3 className="font-semibold mb-1">Câmera indisponível</h3>
                  <p className="text-xs text-ink-600 mb-3">{erroCamera}</p>
                  <button
                    onClick={() => setModo('teclado')}
                    className="btn btn-primary w-full"
                  >
                    Usar teclado
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <form onSubmit={enviarTeclado} className="w-full max-w-sm p-4">
            <label className="text-white text-sm mb-2 block">Código ou EAN</label>
            <input
              autoFocus
              value={valorTeclado}
              onChange={e => setValorTeclado(e.target.value)}
              className="w-full px-4 py-3 rounded-lg text-lg font-mono bg-white text-ink-900"
              placeholder="Ex: 7891234567890"
            />
            <button type="submit" disabled={!valorTeclado.trim()}
              className="btn btn-primary w-full mt-3 py-3">
              Confirmar
            </button>
          </form>
        )}
      </div>

      {/* Controles */}
      <div className="p-3 bg-black/70 flex items-center gap-2">
        <button
          onClick={() => setModo(modo === 'camera' ? 'teclado' : 'camera')}
          className="flex-1 py-3 rounded bg-white/10 text-white font-medium text-sm flex items-center justify-center gap-2 active:bg-white/20"
        >
          {modo === 'camera' ? <Keyboard size={16} /> : <Camera size={16} />}
          {modo === 'camera' ? 'Teclado' : 'Câmera'}
        </button>
        {modo === 'camera' && (
          <button
            onClick={toggleTorch}
            className="flex-1 py-3 rounded bg-white/10 text-white font-medium text-sm flex items-center justify-center gap-2 active:bg-white/20"
          >
            {torch ? <FlashlightOff size={16} /> : <Flashlight size={16} />}
            {torch ? 'Lanterna off' : 'Lanterna'}
          </button>
        )}
      </div>
    </div>
  )
}
